import {
  Alert,
  AlertVariants,
  Box,
  ConnectionStatusEnum,
  Flex,
  InlineFormattedNumber,
  MarketColorDot,
  MarketTypeEnum,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  connectionStatusText,
  useCurrenciesContext,
  useMarketAccountsContext,
  useMarketsContext,
  useObservableValue,
  type BuyingPower,
  type Security,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useBalances, useFeatureFlag } from '../../../../hooks';
import type { BalancesFilter } from '../../../../types';

interface MarketsListProps {
  security: Security;
  market: string;
  buyingPower: BuyingPower | undefined;
  connectionStatus: ConnectionStatusEnum | undefined;
}

export function MarketStatus({ security, market, buyingPower, connectionStatus }: MarketsListProps) {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const { canViewCreditAndBalances } = useFeatureFlag();

  const baseCurrency = currenciesBySymbol.get(security?.BaseCurrency ?? '');
  const ma = marketAccountsByName.get(market);
  const marketName = ma?.Market ?? market;
  const m = marketsByName.get(marketName);
  const marketDisplayName = ma?.DisplayName ?? m?.DisplayName ?? market;

  const currency = buyingPower?.Currency;
  const maxBuy = buyingPower?.getMaxBuy();
  const maxSell = buyingPower?.getMaxSell();

  const balancesFilter: BalancesFilter = useMemo(
    () => ({
      MarketAccounts: compact([ma?.Name]),
      Currencies: compact([security.BaseCurrency]),
    }),
    [ma, security]
  );

  const { balancesByMarketAccountIDCurrencyObs } = useBalances({
    filter: balancesFilter,
    tag: 'MarketStatus',
    openStreamWithoutFilter: false,
  });
  const balancesByMarketAccountIDCurrency = useObservableValue(
    () => balancesByMarketAccountIDCurrencyObs,
    [balancesByMarketAccountIDCurrencyObs]
  );

  const marketAccountBalance = useMemo(() => {
    if (m?.Type === MarketTypeEnum.Dealer && ma?.MarketAccountID) {
      const marketAccountBalances = balancesByMarketAccountIDCurrency?.get(ma.MarketAccountID);
      if (security.BaseCurrency && marketAccountBalances) {
        const balance = marketAccountBalances.get(security.BaseCurrency);
        return balance?.AvailableAmount ?? '';
      }
    }
  }, [m, balancesByMarketAccountIDCurrency, ma?.MarketAccountID, security?.BaseCurrency]);

  return (
    <Box w="300px">
      <Table w="100%">
        <Tbody>
          <Tr>
            <Td>
              <Text color="colors.gray.080" size="fontSizeSmall">
                {marketDisplayName}
              </Text>
            </Td>
            <Td>
              <Flex w="100%" justifyContent="flex-end">
                <MarketColorDot market={marketName} />
              </Flex>
            </Td>
          </Tr>
          {canViewCreditAndBalances && (
            <Tr>
              <Td>Buying Power</Td>
              <Td>
                <Flex w="100%" justifyContent="flex-end">
                  {maxBuy ? (
                    <InlineFormattedNumber
                      number={maxBuy}
                      round
                      currency={currency}
                      increment={baseCurrency?.DefaultIncrement}
                    />
                  ) : (
                    '-'
                  )}
                </Flex>
              </Td>
            </Tr>
          )}
          {canViewCreditAndBalances && (
            <Tr>
              <Td>Selling Power</Td>
              <Td>
                <Flex w="100%" justifyContent="flex-end">
                  {maxSell ? (
                    <InlineFormattedNumber
                      number={maxSell}
                      round
                      currency={currency}
                      increment={baseCurrency?.DefaultIncrement}
                    />
                  ) : (
                    '-'
                  )}
                </Flex>
              </Td>
            </Tr>
          )}
          {canViewCreditAndBalances && m?.Type === MarketTypeEnum.Dealer && (
            <Tr>
              <Td>Unsettled</Td>
              <Td>
                <Flex w="100%" justifyContent="flex-end">
                  {marketAccountBalance ? (
                    <InlineFormattedNumber
                      number={marketAccountBalance}
                      round
                      currency={security.BaseCurrency}
                      increment={baseCurrency?.DefaultIncrement}
                    />
                  ) : (
                    '-'
                  )}
                </Flex>
              </Td>
            </Tr>
          )}
          {connectionStatus && connectionStatus !== ConnectionStatusEnum.Online && (
            <Tr>
              <Td colSpan={2}>
                <Alert dismissable={false} variant={AlertVariants.Warning}>
                  {connectionStatusText(connectionStatus)}
                </Alert>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
